import React from 'react';
import { useOrganizationClientRecords } from '../../../../hooks/api/useOrganizationClientRecords';
import useOrganizationInvitationDefinitions, {
    isConsentCheckboxInput,
} from '../../../../hooks/api/useOrganizationInvitationDefinitions';
import useOrganizationUsers from '../../../../hooks/api/useOrganizationUsers';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import LabelValueDisplay from '../../../v2BaseComponents/LabelValueDisplay';
import { getIntl } from '../../../../utils/intl';
import getName from '../../../../utils/getName';
import getDateString from '../../../../utils/getDateString';
import { APITypes, OrganizationTypes } from '../../../../primary-record-types';
import InvitationFields from './InvitationInputs';
import InvitationFiles from './InvitationFiles';
import InvitationInputFiles from './InvitationInputFiles';
import { clientRecordDetailsToClientRecord, clientRecordStatusTypeGuard } from '../../../../utils/clientRecordsUtil';

const intl = getIntl('client-record-invitation-details', {
    invitationDefinition: 'Invitation Template',
    groups: 'Groups',
    inviter: 'Inviter',
    sentAt: 'Sent At',
    pointOfContact: 'Point of Contact',
    pointOfContactEmail: 'Point of Contact Email',
    client: 'Client',
    clientDoB: 'Client Date of Birth',
    expirationDate: 'Invitation Expiration Date',
    consentItems: 'Consent Items',
    consentFiles: 'Consent Files',
    invitationInputFiles: 'Invitation Input Files',
});

type InviteOrRecordInvitationDetails =
    | APITypes.organizationService.OrganizationClientInvitationDetails
    | OrganizationTypes.OrganizationClientRecordInvitationDetails;

type GetForType<TValue> = {
    clientRecordInvitation: (value: OrganizationTypes.OrganizationClientRecordInvitationDetails) => TValue;
    invitationDetails: (value: APITypes.organizationService.OrganizationClientInvitationDetails) => TValue;
};

function getValue<TValue>(value: InviteOrRecordInvitationDetails, getForType: GetForType<TValue>): TValue {
    if ('organizationClientInvitationId' in value) {
        return getForType.invitationDetails(value);
    } else {
        return getForType.clientRecordInvitation(value);
    }
}

type ContentProps = {
    invitationDetails: InviteOrRecordInvitationDetails;
    user: APITypes.organizationService.OrganizationUserDetails | null;
    invitationDefinition: OrganizationTypes.OrganizationInvitationDefinition | null;
    clientRecord:
        | OrganizationTypes.OrganizationClientRecord
        | APITypes.organizationService.OrganizationClientRecordDetails;
};

const Content: React.FC<ContentProps> = ({ invitationDetails, user, invitationDefinition, clientRecord }) => {
    return (
        <Stack vertical fullWidth space={contentStackSpacing} testID="invitation-details-content">
            <LabelValueDisplay
                omitEmptyValues
                items={[
                    {
                        label: intl('client'),
                        value: getValue(invitationDetails, {
                            clientRecordInvitation: () => getName(clientRecord.clientDetails),
                            invitationDetails: (value) =>
                                getName({
                                    firstName: value.clientFirstName,
                                    lastName: value.clientLastName,
                                }),
                        }),
                    },
                    {
                        label: intl('clientDoB'),
                        value: invitationDetails
                            ? getDateString(
                                  getValue(invitationDetails, {
                                      clientRecordInvitation: () => clientRecord.clientDetails.dob,
                                      invitationDetails: (value) => value.clientDOB,
                                  }),
                                  'date',
                              )
                            : null,
                    },
                    {
                        label: intl('pointOfContact'),
                        value: invitationDetails
                            ? getName(
                                  getValue(invitationDetails, {
                                      clientRecordInvitation: () => clientRecord.clientOrganizerDetails,
                                      invitationDetails: (value) => ({
                                          firstName: value.clientOrganizerFirstName,
                                          lastName: value.clientOrganizerLastName,
                                      }),
                                  }),
                              )
                            : null,
                    },
                    {
                        label: intl('pointOfContactEmail'),
                        value: getValue(invitationDetails, {
                            clientRecordInvitation: () => clientRecord.clientOrganizerDetails.email,
                            invitationDetails: (value) => value.clientOrganizerEmail,
                        }),
                        linkType: 'email',
                    },
                    {
                        label: intl('inviter'),
                        value: user ? getName(user.user) : null,
                    },
                    {
                        label: intl('sentAt'),
                        value: getDateString(invitationDetails?.sentDateTime, 'date-time'),
                    },
                    {
                        label: intl('invitationDefinition'),
                        value: invitationDefinition?.name || '',
                    },
                    {
                        label: intl('expirationDate'),
                        value: getDateString(
                            getValue(invitationDetails, {
                                clientRecordInvitation: () => null,
                                invitationDetails: (value) => value.expirationDateTime,
                            }),
                            'date',
                        ),
                    },
                    {
                        label: intl('consentFiles'),
                        value: invitationDetails?.files?.length ? (
                            <InvitationFiles
                                files={invitationDetails.files}
                                organizationClientRecordId={clientRecord.organizationClientRecordId}
                                organizationClientInvitationId={getValue(invitationDetails, {
                                    clientRecordInvitation: () => undefined,
                                    invitationDetails: (value) => value.organizationClientInvitationId,
                                })}
                            />
                        ) : null,
                    },
                    {
                        label: intl('consentItems'),
                        value: invitationDetails?.inputs?.length ? (
                            <InvitationFields
                                inputs={invitationDetails.inputs.filter(isConsentCheckboxInput)}
                                clientRecord={clientRecordDetailsToClientRecord(clientRecord)}
                            />
                        ) : null,
                    },
                    {
                        label: intl('invitationInputFiles'),
                        value: getValue(invitationDetails, {
                            clientRecordInvitation: (value) =>
                                value.fileInputs?.length ? (
                                    <InvitationInputFiles
                                        files={value.fileInputs}
                                        organizationClientRecordId={clientRecord.organizationClientRecordId}
                                    />
                                ) : null,
                            invitationDetails: () => null,
                        }),
                    },
                ]}
            />
        </Stack>
    );
};

export type ClientRecordInvitationDetailsOwnProps = {
    clientRecordId: string;
};

export type ClientRecordInvitationDetailsProps = ClientRecordInvitationDetailsOwnProps;

const ClientRecordInvitationDetails: React.FC<ClientRecordInvitationDetailsProps> = (props) => {
    const { clientRecord, isLoadingClientRecord } = useOrganizationClientRecords({
        organizationClientRecordId: props.clientRecordId,
    });
    const { isLoadingClientRecordInvitation, clientRecordInvitation } = useOrganizationClientRecords({
        organizationClientRecordId: props.clientRecordId,
        loadClientRecordInvitation: clientRecord?.status === 'invited',
    });
    const { invitationDefinitions, isLoadingInvitationDefinitions } = useOrganizationInvitationDefinitions({});
    const invitationDetails = ((): InviteOrRecordInvitationDetails | null => {
        if (!clientRecord) return null;
        const asClientRecord = clientRecordDetailsToClientRecord(clientRecord);

        if (clientRecordStatusTypeGuard.invited(asClientRecord)) {
            return clientRecordInvitation;
        }

        if (
            clientRecordStatusTypeGuard.inactive(asClientRecord) ||
            clientRecordStatusTypeGuard.active(asClientRecord) ||
            clientRecordStatusTypeGuard.invitationRejected(asClientRecord)
        ) {
            return asClientRecord.invitationDetails;
        }

        return null;
    })();
    const { isLoadingUser, user } = useOrganizationUsers({
        organizationUserId: clientRecordInvitation?.inviterOrganizationUserId || undefined,
    });

    const isLoading =
        isLoadingClientRecordInvitation || isLoadingInvitationDefinitions || isLoadingUser || isLoadingClientRecord;
    const invitationInvitationDefinition =
        invitationDefinitions.find(
            (definition) =>
                definition.organizationInvitationDefinitionId ===
                clientRecordInvitation?.organizationInvitationDefinitionId,
        ) || null;

    return (
        <AnimateLoadedContent isLoading={isLoading || !invitationDetails || !clientRecord}>
            {() => (
                <Content
                    invitationDetails={invitationDetails!}
                    user={user}
                    invitationDefinition={invitationInvitationDefinition}
                    clientRecord={clientRecord!}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientRecordInvitationDetails);
